import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import http from '../../../utils/http';
import ApiConstants from '../../../constants/apiConstants';
import appConstants from '../../../constants/appConstants';
import { AUTH_ROUTES } from '../../../constants/authRoutes';
import {
  evaluatePasswordRules,
  getAuthContextFromSearch,
  getRequestErrorMessage,
  buildAuthPath,
} from '../../../utils/authUtils';
import {
  AuthActionsStack,
  AuthBackToSignIn,
  AuthFieldHint,
  AuthForm,
  AuthHead,
  AuthPage,
  AuthPasswordField,
  AuthPrimaryButton,
  AuthRequirementList,
  AuthStatusBlock,
} from '../../../components/auth/authUi';

const strings = appConstants.auth.resetPassword;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = useMemo(() => String(searchParams.get('email') || '').trim(), [searchParams]);
  const token = useMemo(
    () => String(searchParams.get('token') || searchParams.get('resetToken') || '').trim(),
    [searchParams],
  );
  const expiry = useMemo(() => String(searchParams.get('expiry') || '').trim(), [searchParams]);
  const authContext = useMemo(
    () => getAuthContextFromSearch(searchParams),
    [searchParams],
  );
  const expiryInSeconds = useMemo(() => Number(expiry), [expiry]);
  const isMissingRequiredParams = !email || !token || !expiry;
  const isExpiryInvalid = !Number.isFinite(expiryInSeconds);
  const isExpired =
    !isMissingRequiredParams &&
    !isExpiryInvalid &&
    Math.floor(Date.now() / 1000) > expiryInSeconds;
  const showLinkExpiredScreen = isMissingRequiredParams || isExpiryInvalid || isExpired;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touchedConfirm, setTouchedConfirm] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const rules = evaluatePasswordRules(newPassword);
  const allRulesPass =
    rules.lengthOk && rules.letterNumberOk && rules.noSpacesOk && rules.noSpecialOk;
  const confirmMismatch =
    touchedConfirm && confirmPassword.length > 0 && confirmPassword !== newPassword;

  async function onSubmit(event) {
    event.preventDefault();
    setSubmitError(null);
    setTouchedConfirm(true);

    if (showLinkExpiredScreen) {
      setSubmitError(strings.errors.tokenMissing);
      return;
    }
    if (!allRulesPass || newPassword !== confirmPassword) return;

    try {
      setSubmitting(true);
      await http.post(
        ApiConstants.AUTH.RESET_PASSWORD.URL,
        {},
        { email, token, newPassword },
      );
      setSuccess(true);
    } catch (err) {
      setSubmitError(getRequestErrorMessage(err, strings.errors.resetFailed));
    } finally {
      setSubmitting(false);
    }
  }

  const disableSubmit =
    submitting || showLinkExpiredScreen || !allRulesPass || newPassword !== confirmPassword;

  if (success) {
    return (
      <AuthPage>
        <AuthStatusBlock
          variant="success"
          title={strings.successTitle}
          subtitle={strings.successSubtitle}
        />
        <AuthActionsStack>
          <AuthPrimaryButton
            type="button"
            onClick={() => navigate(buildAuthPath(AUTH_ROUTES.LOGIN, authContext), { replace: true })}
          >
            {strings.successCta}
          </AuthPrimaryButton>
        </AuthActionsStack>
      </AuthPage>
    );
  }

  if (showLinkExpiredScreen) {
    return (
      <AuthPage>
        <AuthHead
          title={strings.linkExpiredTitle}
          subtitle={strings.linkExpiredSubtitle}
        />
        <AuthActionsStack>
          <AuthPrimaryButton
            type="button"
            onClick={() => navigate(buildAuthPath(AUTH_ROUTES.FORGOT_PASSWORD, authContext))}
          >
            {strings.linkExpiredCta}
          </AuthPrimaryButton>
          <AuthBackToSignIn />
        </AuthActionsStack>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <AuthHead title={strings.title} subtitle={strings.subtitle} />
      <AuthForm onSubmit={onSubmit}>
        <AuthPasswordField
          id="new-password"
          label={strings.newPasswordLabel}
          value={newPassword}
          onChange={setNewPassword}
          placeholder={strings.newPasswordPlaceholder}
          autoComplete="new-password"
          showLabel={strings.showPassword}
          hideLabel={strings.hidePassword}
          showHint={false}
        />
        <AuthPasswordField
          id="confirm-password"
          label={strings.confirmPasswordLabel}
          value={confirmPassword}
          onChange={setConfirmPassword}
          onBlur={() => setTouchedConfirm(true)}
          placeholder={strings.confirmPasswordPlaceholder}
          error={confirmMismatch ? strings.errors.confirmMismatch : null}
          autoComplete="new-password"
          showLabel={strings.showPassword}
          hideLabel={strings.hidePassword}
        />
        <AuthRequirementList
          items={[
            { ok: rules.lengthOk, label: strings.reqLength },
            { ok: rules.letterNumberOk, label: strings.reqLetterNumber },
            { ok: rules.noSpacesOk, label: strings.reqNoSpaces },
            { ok: rules.noSpecialOk, label: strings.reqNoSpecial },
          ]}
        />
        <AuthFieldHint error={submitError} />
        <AuthPrimaryButton disabled={disableSubmit}>
          {submitting ? strings.submitLoading : strings.submit}
        </AuthPrimaryButton>
      </AuthForm>
    </AuthPage>
  );
}